import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { MaterialKindEnum } from 'core/graphql';

import type {
  IGroupAlertTarget,
  RosterFilter,
  TabId,
} from 'core/utils/groupAlerts';
import type { IRosterSort } from 'core/utils/groupRoster';
import {
  applyAlertNavigation,
  encode,
  parse,
  IGroupDeepLinkState,
} from 'core/utils/groupDeepLink';

/**
 * React wrapper around the pure `core/utils/groupDeepLink` encode/parse logic.
 *
 * Owns the GroupDetail deep-link view state (`?tab/?filter/?q/?sort/?kind`):
 * it reads `location.search`, parses it into an {@link IGroupDeepLinkState},
 * and exposes setters that re-encode the next state and persist it through the
 * router. All parsing/encoding is delegated to the util — this hook adds only
 * the React/`react-router` plumbing.
 *
 * Writes use `history.replace` (not `push`), matching the existing GroupDetail
 * `?tab` behavior, so deep-link changes do not pollute the browser back stack.
 *
 * `applyAlert` runs the util's `applyAlertNavigation`, which **replaces** the
 * tab + roster filter from a single alert target rather than merging filters
 * from multiple targets (design Req 4.7).
 */
export interface IUseGroupDeepLinkState {
  /** The parsed deep-link state, memoized against `location.search`. */
  state: IGroupDeepLinkState;
  /** Switch the active tab. */
  setTab: (tab: TabId) => void;
  /** Set the Students-tab roster filter chip. */
  setRosterFilter: (filter: RosterFilter) => void;
  /** Set the Students-tab roster search query. */
  setRosterQuery: (query: string) => void;
  /** Set (or clear with `null`) the Students-tab roster sort. */
  setRosterSort: (sort: IRosterSort | null) => void;
  /** Set the Materials-tab kind filter (`''` clears it). */
  setMaterialsKind: (kind: '' | MaterialKindEnum) => void;
  /** Navigate from an alert target, replacing (never merging) tab + filter. */
  applyAlert: (target: IGroupAlertTarget) => void;
}

/**
 * Hook owning the GroupDetail URL deep-link state.
 *
 * Parsing of `location.search` is memoized; each setter derives the next state
 * from the current parsed state, re-encodes via the util, and persists it with
 * `history.replace`.
 */
export const useGroupDeepLinkState = (): IUseGroupDeepLinkState => {
  const history = useHistory();
  const location = useLocation();

  const state = useMemo<IGroupDeepLinkState>(() => parse(location.search), [
    location.search,
  ]);

  /** Encode the next state and persist it via `history.replace`. */
  const commit = useCallback(
    (next: IGroupDeepLinkState) => {
      const params = encode(next);
      history.replace({ ...location, search: params.toString() });
    },
    [history, location],
  );

  const setTab = useCallback((tab: TabId) => commit({ ...state, tab }), [
    commit,
    state,
  ]);

  const setRosterFilter = useCallback(
    (rosterFilter: RosterFilter) => commit({ ...state, rosterFilter }),
    [commit, state],
  );

  const setRosterQuery = useCallback(
    (rosterQuery: string) => commit({ ...state, rosterQuery }),
    [commit, state],
  );

  const setRosterSort = useCallback(
    (rosterSort: IRosterSort | null) => commit({ ...state, rosterSort }),
    [commit, state],
  );

  const setMaterialsKind = useCallback(
    (materialsKind: '' | MaterialKindEnum) =>
      commit({ ...state, materialsKind }),
    [commit, state],
  );

  const applyAlert = useCallback(
    (target: IGroupAlertTarget) => commit(applyAlertNavigation(state, target)),
    [commit, state],
  );

  return {
    state,
    setTab,
    setRosterFilter,
    setRosterQuery,
    setRosterSort,
    setMaterialsKind,
    applyAlert,
  };
};
